import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Button,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  DataGrid,
  GridToolbar,
  GridActionsCellItem,
} from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { formatDate, formatDateTime } from '../../../utils/formatters.js';

const typeColors = {
  Call: 'primary',
  Meeting: 'secondary',
  Email: 'info',
  FollowUp: 'warning',
  Research: 'default',
  General: 'success',
  Other: 'default',
};

function NoteList({
  notes = [],
  loading = false,
  onAdd,
  onEdit,
  onDelete,
  onView,
  onImport,
  selectedNote,
  onSelectionChange,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [contactFilter, setContactFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const filteredNotes = notes.filter((note) => {
    const matchesSearch = note.note?.toLowerCase().includes(searchTerm.toLowerCase())
      || note.contact?.toLowerCase().includes(searchTerm.toLowerCase())
      || note.author?.toLowerCase().includes(searchTerm.toLowerCase())
      || (note.tags || []).some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesType = typeFilter === 'all' || note.type === typeFilter;
    const matchesContact = contactFilter === 'all' || note.contact === contactFilter;

    return matchesSearch && matchesType && matchesContact;
  });

  const handleExport = () => {
    const csvContent = [
      ['Contact', 'Date', 'Type', 'Author', 'Tags', 'Note'],
      ...filteredNotes.map((note) => [
        note.contact || '',
        note.date || '',
        note.type || '',
        note.author || '',
        (note.tags || []).join(', '),
        note.note || '',
      ]),
    ].map((row) => row.map((field) => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'notes.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns = [
    {
      field: 'note',
      headerName: 'Note',
      flex: 1,
      minWidth: 300,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
            {params.value?.substring(0, 100)}
            {params.value?.length > 100 && '...'}
          </Typography>
          {params.row.contact && (
            <Typography variant="caption" color="text.secondary">
              Contact:
              {' '}
              {params.row.contact}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'contact',
      headerName: 'Contact',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {params.value || '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={typeColors[params.value] || 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'author',
      headerName: 'Author',
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'date',
      headerName: 'Date',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? formatDate(params.value) : '-'}
        </Typography>
      ),
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {(params.value || []).slice(0, 2).map((tag, index) => (
            <Chip key={index} label={tag} size="small" variant="outlined" />
          ))}
          {(params.value || []).length > 2 && (
            <Chip label={`+${(params.value || []).length - 2}`} size="small" variant="outlined" />
          )}
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? formatDate(params.value) : '-'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<ViewIcon />}
          label="View"
          onClick={() => onView(params.row)}
          color="primary"
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => onEdit(params.row)}
          color="primary"
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => onDelete(params.row)}
          color="error"
        />,
      ],
    },
  ];

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      {/* Header with search and filters */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" component="h2">
            Notes (
            {filteredNotes.length}
            )
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Export Notes">
            <IconButton
              onClick={handleExport}
              disabled={filteredNotes.length === 0}
              color="primary"
              size="small"
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Note">
            <IconButton
              onClick={onAdd}
              color="primary"
              size="small"
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search notes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ minWidth: 250 }}
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="Call">Call</MenuItem>
              <MenuItem value="Meeting">Meeting</MenuItem>
              <MenuItem value="Email">Email</MenuItem>
              <MenuItem value="FollowUp">Follow Up</MenuItem>
              <MenuItem value="Research">Research</MenuItem>
              <MenuItem value="General">General</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Contact</InputLabel>
            <Select
              value={contactFilter}
              label="Contact"
              onChange={(e) => setContactFilter(e.target.value)}
            >
              <MenuItem value="all">All Contacts</MenuItem>
              {Array.from(new Set(notes.map((note) => note.contact).filter(Boolean))).map((contact) => (
                <MenuItem key={contact} value={contact}>{contact}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title="Import Notes">
            <IconButton onClick={onImport} color="primary">
              <MoreIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* DataGrid */}
      <DataGrid
        rows={filteredNotes}
        columns={columns}
        loading={loading}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 25 },
          },
        }}
        checkboxSelection
        disableRowSelectionOnClick
        slots={{
          toolbar: GridToolbar,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: false,
          },
        }}
        sx={{
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
      />
    </Box>
  );
}

export default NoteList;
